/*
 * Copyright © 2021 - Zimproov.
 * All rights reserved.
 */

// Import the JSON:API filter interface.
import { Filter } from "@andromeda/json-api";
// Import the headers interface.
import { XhrHeaders } from "@andromeda/xhr";

/**
 * Type alias for the list of relationships to include.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export type IncludeList = string[];

/**
 * Interface used to describe the list of fields to include.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export interface SparseFieldset {
    /** List of fields to include for every provided type. */
    [type: string]: string[];
}

/**
 * Type alias for the sorting list.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export type SortingList = string[];

/**
 * Options provided for delete operations.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export interface DeleteOptions {
    /** Optional url used to override the target url found within the configuration. */
    url?: string;
    /** Optional path used to override the auto-generated one. */
    path?: string;
    /** Additional search parameters to add to the request. */
    searchParams?: Record<string, string>;
    /** Additional headers to add to the request. */
    headers?: XhrHeaders;
    /** Signal used to abort the request. */
    signal?: AbortSignal;
}

/**
 * Options provided for read operations.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export interface ReadOneOptions extends DeleteOptions {
    /** List of relationships to include in the response. */
    include?: IncludeList;
    /** Lists of fields that must be included for the requested types. */
    fields?: SparseFieldset;
}

/**
 * Options provided for multiple read operations.
 *
 * @author Caillaud Jean-Baptiste
 * @since 0.2.0
 * @version 1
 */
export interface ReadManyOptions extends ReadOneOptions {
    /**
     * Ordered list of the fields that the response should be sorted by.
     * Optionally, a + or a - can be prepended to specify the sorting direction.
     * (+ for ascending, - for descending)
     */
    sort?: SortingList;
    /** Filter that can be applied to the query. */
    filter?: Filter;
    /** Limits the number of items in the reply. */
    limit?: number;
    /** Specifies the index of the first requested item. */
    skip?: number;
}
